import * as React from "react";

import { atom, useAtom } from "jotai";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from "src/components/ui/drawer";
import { useIsMobile } from "src/lib/hooks/useMobile";
import { cn } from "src/lib/utils";

const responsiveDialogAtom = atom({ ready: false, isMobile: false });

function ResponsiveDialog({ children, ...props }) {
  const [state, setState] = useAtom(responsiveDialogAtom);
  const isMobile = useIsMobile();

  React.useEffect(() => {
    if (isMobile !== undefined) setState({ ready: true, isMobile });
  }, [isMobile, setState]);

  if (isMobile === undefined) return null;

  const Root = state.isMobile ? Drawer : Dialog;
  return <Root {...props}>{children}</Root>;
}

// helper to pick component based on atom value
function pick(MobileComp, DesktopComp, defaults = { mobile: "", desktop: "" }) {
  return function Component({
    className,
    mobileClassName,
    desktopClassName,
    ...props
  }) {
    const [state] = useAtom(responsiveDialogAtom);
    if (!state.ready) return null;

    const Impl = state.isMobile ? MobileComp : DesktopComp;
    const variantClass = state.isMobile ? mobileClassName : desktopClassName;
    const variantDefault = state.isMobile ? defaults.mobile : defaults.desktop;

    return (
      <Impl
        className={cn(variantDefault, className, variantClass)}
        {...props}
      />
    );
  };
}

const ResponsiveDialogTrigger = pick(DrawerTrigger, DialogTrigger);
const ResponsiveDialogPortal = pick(DrawerPortal, DialogPortal);
const ResponsiveDialogOverlay = pick(DrawerOverlay, DialogOverlay);
const ResponsiveDialogClose = pick(DrawerClose, DialogClose);
const ResponsiveDialogContent = pick(DrawerContent, DialogContent, {
  mobile: "px-4",
  desktop: "",
});
const ResponsiveDialogHeader = pick(DrawerHeader, DialogHeader, {
  mobile: "px-0",
  desktop: "",
});
const ResponsiveDialogFooter = pick(DrawerFooter, DialogFooter, {
  mobile: "px-0",
  desktop: "",
});
const ResponsiveDialogTitle = pick(DrawerTitle, DialogTitle);
const ResponsiveDialogDescription = pick(DrawerDescription, DialogDescription);

export {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogOverlay,
  ResponsiveDialogPortal,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
};
